/**
 * @author: Codex
 * @date: 2026/6/15
 * @desc: 地区国家级联单选筛选器组件
 */
import React from 'react';
import classNames from 'classnames';
import { Icon } from 'tea-component';
import { useIntl } from 'umi';
import CascaderSelect from '@home/components/CascaderSelect';
import { getCountryFlag } from '@home/_config/countries';
import EllipsisHover from '@home/components/EllipsisHover';
import useRegionCascaderSelect from './useRegionCascaderSelect';
import { getCountryName, getRegionChangeContext } from './utils';

import type {
  ICascaderSelectChangeContext,
  ICascaderSelectOption,
  ICascaderSelectOverallGroup,
  ICascaderSelectProps,
} from '@home/components/CascaderSelect';
import type { ICountry, IRegion, IBaseProps } from '@home/types/common';

import './index.scss';

export interface IRegionCascaderSelectOption extends ICascaderSelectOption {
  region?: IRegion;
  country?: ICountry;
}

export interface IRegionCascaderSelectChangeContext {
  region?: IRegion;
  country?: ICountry;
  path: IRegionCascaderSelectOption[];
}

export interface IRegionCascaderSelectProps
  extends IBaseProps,
  Pick<
  ICascaderSelectProps,
  'disabled' | 'clearable' | 'boxClassName' | 'overlayClassName' | 'emptyText' | 'searchPlaceholder' | 'levelTitles'
  > {
  label?: string | React.ReactNode;
  value?: string;
  regions: IRegion[];
  overallGroup?: ICascaderSelectOverallGroup;
  multiCountryHref?: string;
  multiCountryText?: React.ReactNode;
  onClickMultiCountry?: () => void;
  onChange?: (value: string, context: IRegionCascaderSelectChangeContext) => void;
}

const RegionCascaderSelect: React.FC<IRegionCascaderSelectProps> = (props) => {
  const {
    style,
    className,
    locale,
    label,
    placeholder,
    value,
    regions = [],
    overallGroup,
    disabled = false,
    clearable = false,
    multiCountryHref,
    multiCountryText,
    onClickMultiCountry,
    onChange,
    ...restProps
  } = props;
  const { formatMessage } = useIntl();

  const {
    currentLocale,
    labelText,
    placeholderText,
    multiCountryTextContent,
    firstLevelTitleText,
    secondLevelTitleText,
    options,
    selectedContext,
  } = useRegionCascaderSelect({
    locale,
    label,
    placeholder,
    multiCountryText,
    regions,
    value,
  });

  const overallOptions = overallGroup?.options ?? [];
  const selectedOverallOption = overallOptions.find((option) => option.value === value);

  const handleChange = (nextValue: string, context: ICascaderSelectChangeContext) => {
    onChange?.(nextValue, getRegionChangeContext(context));
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.('', {
      path: [],
    });
  };

  const handleClickMultiCountry = (event: React.MouseEvent) => {
    if (!onClickMultiCountry) {
      return;
    }

    event.preventDefault();
    onClickMultiCountry();
  };

  const renderButton = () => {
    const { country } = selectedContext || {};
    let countryName = selectedOverallOption?.text || placeholderText;
    let flag = null;

    if (country?.country_code) {
      countryName = getCountryName(country, currentLocale);
      flag = <img className="dbh-region-cascader-select__flag" src={getCountryFlag(country.country_code)} alt="" />;
    }

    let buttonValue = <span className="dbh-region-cascader-select__button-value">{countryName}</span>;

    if (typeof countryName === 'string') {
      buttonValue = <EllipsisHover className="dbh-region-cascader-select__button-value" text={countryName} />;
    }

    return (
      <div className="dbh-region-cascader-select__button">
        {labelText && <span className="dbh-region-cascader-select__button-label">{labelText}</span>}
        {flag}
        {buttonValue}
        {clearable && value && !disabled && (
          <Icon className="dbh-region-cascader-select__button-clear" type="dismiss" onClick={handleClear} />
        )}
      </div>
    );
  };

  const renderFooter = () => {
    if (!multiCountryHref && !onClickMultiCountry) {
      return null;
    }

    return (
      <a
        className="dbh-region-cascader-select__footer-link"
        href={multiCountryHref || '#'}
        onClick={handleClickMultiCountry}
      >
        {multiCountryTextContent}
      </a>
    );
  };

  return (
    <CascaderSelect
      {...restProps}
      style={style}
      className={classNames('dbh-region-cascader-select', className)}
      label={labelText}
      placeholder={placeholderText}
      value={value}
      options={options}
      overallGroup={overallGroup}
      renderLeafContent={overallOptions.length ? (option) => (
        option.value === 'global' ? formatMessage({ id: 'dbi_info_global_tips_store_rank_top' }) : null
      ) : undefined}
      disabled={disabled}
      clearable={clearable}
      firstLevelTitle={firstLevelTitleText}
      secondLevelTitle={secondLevelTitleText}
      footer={renderFooter()}
      button={renderButton}
      onChange={handleChange}
    />
  );
};

export default RegionCascaderSelect;
